import {
  CustomCollectionsFieldConfig,
  Entry,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import { CrossEntryValidation } from "../types";
import shouldDisplayField from "./shouldDisplayField";

function fieldNoun(v: CrossEntryValidation): string {
  return v.fieldLabel || v.fieldKey;
}

/**
 * Validate a single entry's values against cross-entry validation rules.
 * Returns an error message string if validation fails, or null if valid.
 *
 * Used by EntryCard on save (per-entry) and useStepLogic on next (global).
 *
 * Rules are skipped when the parent field is hidden for the entry being
 * validated — a user can't satisfy a checkbox they can't see.
 */
export default function validateCrossEntryRules({
  entryId,
  values,
  validations,
  getUniqueConflict,
  getCrossEntryTotal,
  getCrossEntryCount,
  application,
  allFieldConfigs,
}: {
  entryId: number;
  values: Partial<Entry>;
  validations: CrossEntryValidation[];
  getUniqueConflict?: (
    entryId: number,
    fieldKey: string,
    value: string
  ) => string | null;
  getCrossEntryTotal?: (fieldKey: string, excludeId?: number) => number;
  getCrossEntryCount?: (fieldKey: string, excludeId?: number) => number;
  application?: OnboardingApplication;
  allFieldConfigs?: CustomCollectionsFieldConfig[];
}): string | null {
  const isHiddenForThisEntry = (v: CrossEntryValidation): boolean => {
    if (!v.fieldConfig) return false;
    return !shouldDisplayField(
      v.fieldConfig,
      values,
      application,
      undefined,
      allFieldConfigs
    );
  };

  if (getUniqueConflict) {
    for (const v of validations) {
      if (v.rule !== "unique") continue;
      if (isHiddenForThisEntry(v)) continue;
      const val = values[v.fieldKey];
      if (!val) continue;
      const conflict = getUniqueConflict(entryId, v.fieldKey, String(val));
      if (conflict) {
        return (
          v.message ||
          `This ${fieldNoun(v)} is already associated with ${conflict}`
        );
      }
    }
  }

  if (getCrossEntryTotal && getCrossEntryCount) {
    for (const v of validations) {
      if (isHiddenForThisEntry(v)) continue;
      if (v.rule === "maxTotal") {
        const othersTotal = getCrossEntryTotal(v.fieldKey, entryId);
        const thisValue = Number(values[v.fieldKey]) || 0;
        if (othersTotal + thisValue > v.value) {
          return v.message || `${fieldNoun(v)} cannot exceed ${v.value}%`;
        }
      }
      if (v.rule === "maxCount") {
        const othersCount = getCrossEntryCount(v.fieldKey, entryId);
        const thisChecked = values[v.fieldKey] === true ? 1 : 0;
        if (othersCount + thisChecked > v.value) {
          return v.message || `Maximum ${v.value} ${fieldNoun(v)} allowed`;
        }
      }
    }
  }

  return null;
}
